import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as XCircleIcon } from '@/assets/icons/x-circle.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import { FreelancerInvoiceSubmissionResource } from '@/types/resources';
import { api } from '@/utils/api';
import { InvoiceGenerationStatus } from '@/utils/enums';

interface Props {
  submission: FreelancerInvoiceSubmissionResource;
}

/** Cancel / edit actions the freelancer has over their own submission. */
const FreelancerInvoiceRowActions: FC<Props> = ({ submission }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const isPending = submission.status === InvoiceGenerationStatus.Pending;
  const isNeedsAction =
    submission.status === InvoiceGenerationStatus.Needs_Action;

  const handleCancel = async () => {
    const confirmed = await alerts.confirm(
      'Are you sure you want to cancel this invoice?',
    );
    if (!confirmed) return;
    try {
      await api.post(
        `/v1/payments/freelancer-invoice-submissions/${submission.id}/cancel`,
      );
      await queryClient.invalidateQueries(['freelancer_invoice_submissions'], {
        exact: false,
      });
      alerts.success('Invoice cancelled successfully');
    } catch {
      alerts.error();
    }
  };

  if (!isPending && !isNeedsAction) return null;

  return (
    <ActionsWrap>
      <ActionsDropdown>
        {isPending && (
          <ActionsDropdownButton as="button" onClick={handleCancel}>
            <XCircleIcon />
            Cancel Invoice
          </ActionsDropdownButton>
        )}
        {isNeedsAction && (
          <ActionsDropdownButton
            as="button"
            onClick={() =>
              navigate('/general/freelancers/invoices/generate', {
                state: { submission },
              })
            }
          >
            <PencilIcon />
            Edit
          </ActionsDropdownButton>
        )}
      </ActionsDropdown>
    </ActionsWrap>
  );
};

export default FreelancerInvoiceRowActions;
